import React, { useState, useEffect } from 'react';
import Home from './components/Home';
import CreateTournament from './components/CreateTournament';
import TournamentDashboard from './components/TournamentDashboard';
import GlobalPlayerManagement from './components/GlobalPlayerManagement';
import PlayerSelector from './components/PlayerSelector';
import { 
  initDB, 
  saveTournamentDB, 
  loadTournamentDB, 
  getAllTournamentsDB,
  deleteTournamentDB,
  savePlayerDB,
  getAllPlayersDB,
  deletePlayerDB 
} from './utils/database';

function App() {
  const [screen, setScreen] = useState('home');
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [currentTournament, setCurrentTournament] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [tournamentConfig, setTournamentConfig] = useState(null);

  useEffect(() => {
    initDB().then(async () => {
      const [loadedTournaments, loadedPlayers] = await Promise.all([
        getAllTournamentsDB(),
        getAllPlayersDB()
      ]);
      setTournaments(loadedTournaments);
      setPlayers(loadedPlayers);
    });
  }, []);

  const handleCreateTournament = (config) => {
    setTournamentConfig(config);
    setSelectedPlayers([]);
    setScreen('playerSelect');
  };

  const handlePlayerSelectionComplete = () => {
    const newTournament = {
      id: Date.now().toString(),
      name: tournamentConfig.name,
      swissRounds: tournamentConfig.swissRounds,
      hasTopCut: tournamentConfig.hasTopCut,
      topCutSize: tournamentConfig.topCutSize,
      currentRound: 0,
      players: selectedPlayers.map(p => ({
        ...p,
        matchWins: 0,
        matchLosses: 0,
        matchDraws: 0,
        matchPoints: 0,
        gameWins: 0,
        gameLosses: 0,
        gamesPlayed: 0,
        matchesPlayed: 0,
        omw: 0,
        gwp: 0,
        ogw: 0,
        dropped: false
      })),
      rounds: [],
      createdAt: new Date().toISOString(),
      archived: false
    };
    
    saveTournamentDB(newTournament).then(() => {
      setTournaments([newTournament, ...tournaments]);
      setCurrentTournament(newTournament);
      setScreen('tournament');
      setTournamentConfig(null);
      setSelectedPlayers([]);
    });
  };

  const handleLoadTournament = async (id) => {
    const tournament = await loadTournamentDB(id);
    setCurrentTournament(tournament);
    setScreen('tournament');
  };

  const handleUpdateTournament = (updated) => {
    saveTournamentDB(updated).then(() => {
      setCurrentTournament(updated);
      setTournaments(tournaments.map(t => t.id === updated.id ? updated : t));
    });
  };

  const handleDeleteTournament = async (id) => {
    await deleteTournamentDB(id);
    setTournaments(tournaments.filter(t => t.id !== id));
  };

  const handleArchiveTournament = async (id) => {
    const tournament = tournaments.find(t => t.id === id);
    if (tournament) {
      const archived = { ...tournament, archived: true };
      await saveTournamentDB(archived);
      setTournaments(tournaments.map(t => t.id === id ? archived : t));
    }
  };

  const handleAddPlayer = async (name) => {
    const newPlayer = {
      id: Date.now().toString(),
      name,
      createdAt: new Date().toISOString()
    };
    await savePlayerDB(newPlayer);
    setPlayers([...players, newPlayer]);
  };

  const handleDeletePlayer = async (id) => {
    await deletePlayerDB(id);
    setPlayers(players.filter(p => p.id !== id));
  };

  const handleTogglePlayerSelection = (player) => {
    setSelectedPlayers(prev => {
      const exists = prev.find(p => p.id === player.id);
      if (exists) {
        return prev.filter(p => p.id !== player.id);
      } else {
        return [...prev, player];
      }
    });
  };

  const handleAddNewPlayerToSelection = async (name) => {
    const newPlayer = {
      id: Date.now().toString(),
      name,
      createdAt: new Date().toISOString()
    };
    await savePlayerDB(newPlayer);
    setPlayers([...players, newPlayer]);
    setSelectedPlayers([...selectedPlayers, newPlayer]);
  };

  if (screen === 'create') {
    return (
      <CreateTournament
        onBack={() => setScreen('home')}
        onCreate={handleCreateTournament}
      />
    );
  }

  if (screen === 'playerSelect') {
    return (
      <PlayerSelector
        availablePlayers={players}
        selectedPlayers={selectedPlayers}
        onTogglePlayer={handleTogglePlayerSelection}
        onAddNewPlayer={handleAddNewPlayerToSelection}
        onBack={() => setScreen('home')}
        onContinue={handlePlayerSelectionComplete}
      />
    );
  }

  if (screen === 'globalPlayers') {
    return (
      <GlobalPlayerManagement
        players={players}
        onAddPlayer={handleAddPlayer}
        onDeletePlayer={handleDeletePlayer}
        onBack={() => setScreen('home')}
      />
    );
  }

  if (screen === 'tournament' && currentTournament) {
    return (
      <TournamentDashboard
        tournament={currentTournament}
        onUpdate={handleUpdateTournament}
        onBack={() => {
          setScreen('home');
          setCurrentTournament(null);
        }}
        availablePlayers={players}
        onAddPlayer={handleAddPlayer}
      />
    );
  }

  return (
    <Home
      onNewTournament={() => setScreen('create')}
      onLoadTournament={handleLoadTournament}
      onDeleteTournament={handleDeleteTournament}
      onArchiveTournament={handleArchiveTournament}
      tournaments={tournaments}
      onManagePlayers={() => setScreen('globalPlayers')}
      players={players}
    />
  );
}

export default App;
